import React, { useEffect, useState } from 'react';
import { Container, Button, Badge, Card, Row, Col } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { usePageLoader } from '../context/PageLoaderContext';
import ProductCard from '../components/product/ProductCard';
import './MyReturns.css';

const MyReturns = () => {
  const { isAuthenticated } = useAuth();
  const { setPageLoading } = usePageLoader();
  const navigate = useNavigate();
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bestsellers, setBestsellers] = useState([]);
  const [loadingBestsellers, setLoadingBestsellers] = useState(false);

  useEffect(() => {
    setPageLoading(loading);
    return () => setPageLoading(false);
  }, [loading, setPageLoading]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchReturns();
    }
    fetchBestsellers();
  }, [isAuthenticated]);

  const fetchReturns = async () => {
    try {
      setLoading(true);
      const response = await api.get('/returns/my-returns');
      setReturns(response.returns || []);
    } catch (error) {
      console.error('Error fetching returns:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBestsellers = async () => {
    try {
      setLoadingBestsellers(true);
      const response = await api.get('/products/bestsellers');
      setBestsellers(response.products || []);
    } catch (error) {
      console.error('Error fetching bestsellers:', error);
    } finally {
      setLoadingBestsellers(false);
    }
  };

  // Generate return number from ID
  const getReturnNumber = (id, createdAt) => {
    const year = new Date(createdAt).getFullYear();
    const paddedId = String(id).padStart(3, '0');
    return `RET-${year}-${paddedId}`;
  };

  // Get status badge styling
  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'Pending', className: 'status-pending', bgColor: '#FFF3CD', textColor: '#856404' },
      approved: { label: 'Approved', className: 'status-approved', bgColor: '#D1E7DD', textColor: '#0F5132' },
      refund_initialized: { label: 'Initialized', className: 'status-initialized', bgColor: '#CFE2FF', textColor: '#084298' },
      refunded: { label: 'Refunded', className: 'status-refunded', bgColor: '#E2D9F3', textColor: '#6F42C1' },
      return_cancelled: { label: 'Cancelled', className: 'status-cancelled', bgColor: '#F8D7DA', textColor: '#842029' },
    };

    const statusInfo = statusMap[status] || { label: status, className: 'status-default', bgColor: '#E9ECEF', textColor: '#495057' };
    return (
      <Badge 
        bg='none'
        className={`${statusInfo.className} status-badge`}
        style={{ 
          backgroundColor: statusInfo.bgColor,
          color: statusInfo.textColor,
          border: 'none'
        }}
      >
        {statusInfo.label}
      </Badge>
    );
  };

  // Helper function to get image URL
  const getImageUrl = (imagePath) => {
    if (!imagePath) return '/placeholder.png';
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    if (imagePath.startsWith('/')) {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const baseUrl = API_BASE_URL.replace(/\/api$/, '');
      return `${baseUrl}${imagePath}`;
    }
    return imagePath;
  };

  // Format date as dd/mm/yyyy
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  if (loading) return null;

  return (
    <div className="returns-page">
      <Container>
        <h1 className="returns-title">Return History</h1>

        {returns.length === 0 ? (
          <div className="empty-returns-section">
            <Card className="empty-returns-card">
              <Card.Body className="text-center py-5">
                <p className="empty-returns-message">You have no return requests yet.</p>
                <Link to="/orders">
                  <Button variant="primary" className="view-orders-btn">View Orders</Button>
                </Link>
              </Card.Body>
            </Card>

            {/* Bestsellers Section */}
            {bestsellers.length > 0 && (
              <div className="bestsellers-section mt-5">
                <h2 className="bestsellers-title">Bestsellers</h2>
                {loadingBestsellers ? (
                  <div className="text-center py-4">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <Row>
                    {bestsellers.map((product) => (
                      <Col key={product.id} md={4} sm={6} xs={6} lg={3} className="mb-4">
                        <ProductCard product={product} buttonText="Add to Cart" />
                      </Col>
                    ))}
                  </Row>
                )}
                <div className="text-center mt-4">
                  <Link to="/products?filter=bestsellers">
                    <Button variant="outline-dark" className="show-more-btn">
                      Show More
                    </Button>
                  </Link>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="returns-cards-list">
            {returns.map((returnRequest) => (
              <Card key={returnRequest.id} className="return-card" onClick={() => navigate(`/returns/detail/${returnRequest.id}`)}>
                <Card.Body className="return-card-body">
                  <div className="return-card-header">
                    <span className="return-card-number">{getReturnNumber(returnRequest.id, returnRequest.created_at)}</span>
                    {getStatusBadge(returnRequest.status)}
                  </div>
                  <div className="return-card-main">
                    <div className="return-card-product">
                      <div className="return-card-image-wrap">
                        <img
                          src={getImageUrl(returnRequest.product_image)}
                          alt={returnRequest.product_name}
                          onError={(e) => { e.target.src = '/placeholder.png'; }}
                        />
                      </div>
                      <div className="return-card-details">
                        <h6 className="return-card-product-name">
                          {returnRequest.product_name}
                          {(returnRequest.item_type === 'combo' || returnRequest.combo_id) && (
                            <span className="ms-2 badge" style={{ background: '#16a34a', fontSize: 10 }}>COMBO</span>
                          )}
                        </h6>
                        {(returnRequest.item_type === 'combo' || returnRequest.combo_id) && (
                          <div className="small text-muted mb-1">
                            {returnRequest.combo_includes_label ||
                              (returnRequest.combo_selections_detail || [])
                                .map((d) => d.product_name)
                                .filter(Boolean)
                                .join(' + ')}
                          </div>
                        )}
                        {(returnRequest.item_type === 'combo' || returnRequest.combo_id) &&
                          (returnRequest.combo_selections_detail || []).length > 0 && (
                          <div className="mb-2" style={{ display: 'grid', gap: 4 }}>
                            {(returnRequest.combo_selections_detail || []).map((d, idx) => (
                              <div key={d.variant_id || idx} className="small text-muted">
                                • {d.product_name}
                                {[d.color_name, d.size].filter(Boolean).length > 0
                                  ? ` (${[d.color_name, d.size].filter(Boolean).join(' • ')})`
                                  : ''}
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="return-card-meta">
                          <span className="return-meta-item">
                            <strong>Request date:</strong> {formatDate(returnRequest.created_at)}
                          </span>
                          <span className="return-meta-item">
                            <strong>Qty:</strong> {returnRequest.quantity || 1}
                          </span>
                          <span className="return-meta-item">
                            <strong>Reason:</strong> {returnRequest.return_reason || 'N/A'}
                          </span>
                        </div>
                        <div className="return-card-refund">
                          Refund amount: <strong>₹{(parseFloat(returnRequest.amount_refund) || 0).toFixed(2)}</strong>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="return-card-actions" onClick={(e) => e.stopPropagation()}>
                    <Button
                      variant="dark"
                      size="sm"
                      className="return-card-view-detail"
                      onClick={() => navigate(`/returns/detail/${returnRequest.id}`)}
                    >
                      View details
                    </Button>
                    <Link
                      to={
                        returnRequest.item_type === 'combo' || returnRequest.combo_id
                          ? `/combos/${returnRequest.combo_id}`
                          : `/products/${returnRequest.product_id}`
                      }
                      className="return-card-link"
                    >
                      {returnRequest.item_type === 'combo' || returnRequest.combo_id
                        ? 'View Combo'
                        : 'View Product'}
                    </Link>
                    <Link to="/contact" className="return-card-link return-card-link-support">
                      Contact Support
                    </Link>
                  </div>
                </Card.Body>
              </Card>
            ))}
          </div>
        )}
      </Container>
    </div>
  );
};

export default MyReturns;
